import { Activity, CircleCheck, Loader2, XCircle } from "lucide-react";
import { useHealth, useAdminStats } from "@/lib/api/stats.queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { formatDate } from "@/lib/utils";

export function HealthDashboard() {
  const health = useHealth();
  const stats = useAdminStats();
  const s = stats.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Salud API</h1>
        <p className="text-sm text-slate-500 mt-1">Estado del sistema y métricas de plataforma.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Estado del worker</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              {health.isPending ? (
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              ) : health.isSuccess ? (
                <CircleCheck className="h-8 w-8 text-emerald-500" />
              ) : (
                <XCircle className="h-8 w-8 text-red-500" />
              )}
              <div>
                <p className="text-lg font-semibold text-slate-900">
                  {health.isPending ? "Verificando" : health.isSuccess ? "Operacional" : "Caído"}
                </p>
                {health.data && (
                  <p className="text-xs text-slate-500 font-mono">
                    {formatDate(health.data.timestamp)}
                  </p>
                )}
                {health.isError && (
                  <p className="text-xs text-red-600">
                    {health.error instanceof Error ? health.error.message : "Error de conexión"}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Servicio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-sm text-slate-700">
              <span className="font-medium">Nombre:</span>{" "}
              {health.data?.service ?? "pahfacturar-api"}
            </p>
            <p className="text-sm text-slate-700">
              <span className="font-medium">URL base:</span>{" "}
              <span className="font-mono text-xs">
                {import.meta.env.VITE_API_BASE_URL ??
                  (import.meta.env.DEV
                    ? "http://localhost:8580"
                    : "https://api.pahfacturar.com")}
              </span>
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-brand-600" />
              Métricas de plataforma
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.isPending ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                <span className="text-sm text-slate-500">Cargando estadísticas…</span>
              </div>
            ) : stats.isError ? (
              <p className="text-sm text-red-600">No se pudieron cargar las estadísticas.</p>
            ) : s ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                    Equipos
                  </p>
                  <p className="text-2xl font-semibold text-slate-900 mt-1">{s.teamsCount}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                    Empresas
                  </p>
                  <p className="text-2xl font-semibold text-slate-900 mt-1">{s.companiesCount}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                    Suscripciones activas
                  </p>
                  <p className="text-2xl font-semibold text-slate-900 mt-1">
                    {s.activeSubscriptions}
                  </p>
                </div>
                <div className="col-span-full">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">
                    Procesos DIAN — últimas 24 h
                  </p>
                  <div className="flex gap-6">
                    {(
                      [
                        { label: "En cola", key: "queued", color: "bg-amber-400" },
                        { label: "Procesando", key: "processing", color: "bg-sky-400" },
                        { label: "Completados", key: "completed", color: "bg-emerald-500" },
                        { label: "Fallidos", key: "failed", color: "bg-red-500" },
                      ] as const
                    ).map(({ label, key, color }) => (
                      <div key={key} className="flex items-center gap-2">
                        <div className={`h-2.5 w-2.5 rounded-full ${color}`} />
                        <div>
                          <p className="text-[11px] text-slate-500">{label}</p>
                          <p className="text-sm font-semibold text-slate-900">
                            {s.dianProcessesLast24h[key]}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {s.lastProcessAt && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                      Último proceso
                    </p>
                    <p className="text-sm text-slate-700 mt-1 font-mono">
                      {formatDate(s.lastProcessAt)}
                    </p>
                  </div>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
    </div>
  );
}
